import React from 'react';
import {
  Form,
  useActionData,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
  useOutlet,
  useRevalidator,
  useSubmit
} from 'react-router-dom';
import * as Headless from '@headlessui/react';

import { Await } from '@oysterjs/ui/async';
import {
  AgencySubscriptionFieldsFragment,
  AgencyUserFieldsFragment,
  LoggedInAgencyUserFieldsFragment,
  SubscriptionState,
  SubscriptionPlan,
  SubscriptionPeriod,
  GetSubscriptionUpdatePaymentLinkQueryVariables,
  GetSubscriptionUpdatePaymentLinkDocument,
  GetSubscriptionUpdatePaymentLinkQuery,
  AgencyUserState,
  AgencyUserInput,
  CreateAgencyUserMutation,
  GetSubscriptionUpdateLinkQuery,
  GetSubscriptionUpdateLinkQueryVariables,
  GetSubscriptionUpdateLinkDocument,
  PreviewUpdateSubscriptionResultFieldsFragment,
  UpdateSubscriptionDocument,
  UpdateSubscriptionMutation,
  UpdateSubscriptionMutationVariables,
  Role,
  Resource,
  Action,
  Scope,
  SubscriptionEntitlement,
  ChildAgencyFieldsFragment,
  useGetAgencyProfileOptionsQuery,
  useGetTeamsQuery
} from '~/api/graphql';
import { Spinner } from '@oysterjs/ui/spinner';
import { Button, PrimaryButton } from '@oysterjs/ui/button';
import { Badge } from '@oysterjs/ui/badge';
import { client } from '~/api/client';
import { EllipsisVerticalIcon, MinusIcon, PlusIcon, CheckIcon } from '@heroicons/react/24/outline';
import {
  BottomSheetDialog,
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
  MountableDialog
} from '@oysterjs/ui/dialog';
import { Description, ErrorMessage, FieldGroup } from '@oysterjs/ui/fieldset';
import { Field } from '@oysterjs/ui/fieldset';
import { Label } from '@oysterjs/ui/fieldset';
import { Fieldset } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { ValidationError } from '@oysterjs/core/graphql';
import { useFormActions } from '@oysterjs/core/graphql/form';
import { AnimatePresence } from 'motion/react';
import clsx from 'clsx';
import { ApolloError } from '@apollo/client';
import { PaymentElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Combobox } from '@oysterjs/ui/combobox';
import { usePermissions } from '~/components/permissions';
import { SeatProgressBar } from '~/components/settings/usage/seat-progress-bar';
import {
  buildUserProfileOptions,
  decodeUserProfileOption,
  userProfileChoiceOption,
  userProfileFieldDescription,
  userProfileFieldLabel
} from '~/components/settings/subscription/user-profile-options';
import { UserImport } from '~/components/settings/subscription/user-import';
import {
  PagedUserList,
  UserStateBadge,
  type UserListTeam
} from '~/components/settings/subscription/user-list';
import {
  DisableUserDialog,
  EnableUserDialog,
  EditUserDialog
} from '~/components/settings/subscription/user-dialogs';
import {
  RenewalMigrationBanner,
  RenewalMigrationThankYouBanner
} from '~/components/settings/subscription/renewal-migration-banner';
import {
  formatRenewalPeriodLabel,
  formatRenewalPlanLabel,
  isLegacyStandardActiveSubscription,
  shouldShowRenewalMigrationBanner
} from '~/components/settings/subscription/renewal-migration-shared';
import { dismissStandardSunsetRenewalBanner } from '~/components/settings/subscription/dismiss-renewal-banner';
import { InvoicePreview } from '~/components/settings/subscription/invoice-preview';
import { Link } from '@oysterjs/ui/link';
import { RenewalMigrationWizard } from '~/components/settings/subscription/renewal-migration-wizard';
import { UsageBasedFeaturesNote } from '~/components/settings/subscription/usage-based-features-note';
import {
  getStandardPlanRenewalDate,
  resolveSunsetRenewalPaymentDate
} from '~/components/settings/subscription/renewal-billing-date';
import { AnnualRenewalBanner } from '~/components/settings/subscription/annual-renewal-banner';
import { AnnualRenewalWizard } from '~/components/settings/subscription/annual-renewal-wizard';
import { canSelfServeSubscription } from '~/components/settings/subscription/self-serve';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';

// Re-export shared user dialogs for backward compatibility with external imports
export { DisableUserDialog, EnableUserDialog, EditUserDialog };

// This is included just to capture the unlikely case that minSeats is not set in the subscription
// or the subscription is not found.
const FALLBACK_MIN_SEATS = 3;

const titleCase = (s: string) =>
  s
    .split(/[\s_]/g)
    .map((part) => part[0].toUpperCase() + part.substring(1).toLowerCase())
    .join(' ');

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const stripePublicKey = process.env.STRIPE_PUBLIC_KEY || window.oyster?.opts?.stripePublicKey || '';
const stripe = loadStripe(stripePublicKey);

export const SettingsSubscriptionPage = () => {
  const outlet = useOutlet();
  const revalidator = useRevalidator();
  // Adding a user by hand mid-import could take a seat a later batch was counting on.
  const [importing, setImporting] = React.useState(false);
  const { hasPermission, hasScopedPermission } = usePermissions();
  const userImportEnabled = useFlag(FEATURE_FLAGS.AGENCY_USER_CSV_IMPORT);
  const loader = useLoaderData() as {
    data: Promise<{
      agencyUsers?: AgencyUserFieldsFragment[];
      agencyUser?: LoggedInAgencyUserFieldsFragment;
      childAgencies?: ChildAgencyFieldsFragment[];
    }>;
  };

  const [updatePaymentLoading, setUpdatePaymentLoading] = React.useState(false);
  const [showSelectPlanDialog, setShowSelectPlanDialog] = React.useState(false);
  const [showRenewalWizard, setShowRenewalWizard] = React.useState(false);
  const [showAnnualRenewalWizard, setShowAnnualRenewalWizard] = React.useState(false);

  const [showManageSubscriptionDialog, setShowManageSubscriptionDialog] = React.useState(false);
  const [manageSubscriptionPlan, setManageSubscriptionPlan] = React.useState<
    SubscriptionPlan | undefined
  >();
  const [manageSubscriptionPeriod, setManageSubscriptionPeriod] = React.useState<
    SubscriptionPeriod | undefined
  >();
  const [manageSubscriptionSeats, setManageSubscriptionSeats] = React.useState<
    number | undefined
  >();

  const [disableUserId, setDisableUserId] = React.useState<string | null>(null);
  const [enableUserId, setEnableUserId] = React.useState<string | null>(null);
  const [editUser, setEditUser] = React.useState<AgencyUserFieldsFragment | null>(null);

  const onManageSubscription = async (
    plan: SubscriptionPlan,
    period: SubscriptionPeriod,
    seats?: number
  ) => {
    setManageSubscriptionPlan(plan);
    setManageSubscriptionPeriod(period);
    setManageSubscriptionSeats(seats);
    setShowSelectPlanDialog(false);
    setShowManageSubscriptionDialog(true);
  };

  const onUpdatePayment = async () => {
    setUpdatePaymentLoading(true);

    try {
      const data = await client.query<
        GetSubscriptionUpdatePaymentLinkQuery,
        GetSubscriptionUpdatePaymentLinkQueryVariables
      >({
        query: GetSubscriptionUpdatePaymentLinkDocument
      });

      if (data.errors?.length || !data.data?.subscriptionManagementLinks?.updatePaymentMethodUrl) {
        return;
      }
      window.open(data.data.subscriptionManagementLinks.updatePaymentMethodUrl, '_blank');
    } catch {
      // Error opening payment update - user will see error from backend if they try again
    } finally {
      setUpdatePaymentLoading(false);
    }
  };

  return (
    <>
      <React.Suspense fallback={<SubscriptionPageLoader />}>
        <Await resolve={loader.data}>
          {(data) => {
            if (!data.agencyUser?.agency.subscription) {
              return null;
            }
            const { numSeats, numSeatsActive } = data.agencyUser.agency.subscription;
            const addUserBlocked = importing || numSeatsActive >= numSeats;

            return (
              <div className="space-y-6">
                <SubscriptionStatusBanner
                  subscription={data.agencyUser.agency.subscription}
                  agencyName={data.agencyUser.agency.name}
                  onUpdateSubscription={() => setShowSelectPlanDialog(true)}
                  agencyUsers={data.agencyUsers}
                  onDisableUser={(userId) => setDisableUserId(userId)}
                  readOnly={!hasPermission(Resource.Subscription, Action.Update)}
                  onReviewRenewalOptions={() => setShowRenewalWizard(true)}
                  onDismissRenewalBanner={async () => {
                    await dismissStandardSunsetRenewalBanner();
                    revalidator.revalidate();
                  }}
                  onReviewAnnualRenewal={() => setShowAnnualRenewalWizard(true)}
                />
                <AnnualRenewalWizard
                  open={showAnnualRenewalWizard}
                  onClose={() => setShowAnnualRenewalWizard(false)}
                  onCompleted={() => revalidator.revalidate()}
                  agencyUser={data.agencyUser}
                />
                <SelectPlanDialog
                  open={showSelectPlanDialog}
                  onClose={() => setShowSelectPlanDialog(false)}
                  onSelectPlan={(plan, period, seats) =>
                    onManageSubscription(
                      plan,
                      period,
                      seats ||
                        data.agencyUser?.agency.subscription?.numSeats ||
                        Math.max(
                          data.agencyUser?.agency.subscription?.numSeatsMinimum ??
                            FALLBACK_MIN_SEATS,
                          data.agencyUsers?.filter((user) => user.state === AgencyUserState.Active)
                            .length || 0
                        )
                    )
                  }
                  currentPlan={data.agencyUser.agency.subscription.plan || SubscriptionPlan.Pro}
                  currentPeriod={
                    data.agencyUser.agency.subscription.period || SubscriptionPeriod.Monthly
                  }
                  currentSeats={
                    data.agencyUser?.agency.subscription?.numSeats ||
                    Math.max(
                      data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
                      data.agencyUsers?.filter((user) => user.state === AgencyUserState.Active)
                        .length || 0
                    )
                  }
                  minSeats={Math.max(
                    data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
                    data.agencyUsers?.filter((user) => user.state === AgencyUserState.Active)
                      .length || 0
                  )}
                  includedSeats={
                    data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS
                  }
                />
                <ManageSubscriptionDialog
                  open={showManageSubscriptionDialog}
                  onClose={() => setShowManageSubscriptionDialog(false)}
                  onSubscriptionUpdated={() => {
                    revalidator.revalidate();
                  }}
                  agencyUser={data.agencyUser}
                  plan={
                    manageSubscriptionPlan ||
                    data.agencyUser?.agency.subscription?.plan ||
                    SubscriptionPlan.Pro
                  }
                  period={
                    manageSubscriptionPeriod ||
                    data.agencyUser?.agency.subscription?.period ||
                    SubscriptionPeriod.Monthly
                  }
                  seats={
                    manageSubscriptionSeats ||
                    data.agencyUser?.agency.subscription?.numSeats ||
                    Math.max(
                      data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
                      data.agencyUsers?.filter((user) => user.state === AgencyUserState.Active)
                        .length || 0
                    )
                  }
                  minSeats={Math.max(
                    data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
                    data.agencyUsers?.filter((user) => user.state === AgencyUserState.Active)
                      .length || 0
                  )}
                  includedSeats={
                    data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS
                  }
                  voiceAgents={Math.max(
                    0,
                    data.agencyUser?.agency.subscription?.numVoiceAgents - 1 || 0
                  )}
                />
                <RenewalMigrationWizard
                  open={showRenewalWizard}
                  onClose={() => {
                    setShowRenewalWizard(false);
                  }}
                  onCompleted={() => {
                    revalidator.revalidate();
                  }}
                  agencyUser={data.agencyUser}
                  agencyName={data.agencyUser.agency.name}
                  renewalDate={getStandardPlanRenewalDate(data.agencyUser.agency.subscription)}
                  currentPeriod={
                    data.agencyUser.agency.subscription?.period || SubscriptionPeriod.Monthly
                  }
                  currentSeats={
                    data.agencyUser.agency.subscription?.numSeats ||
                    Math.max(
                      data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
                      data.agencyUsers?.filter((user) => user.state === AgencyUserState.Active)
                        .length || 0
                    )
                  }
                  minSeats={Math.max(
                    data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
                    data.agencyUsers?.filter((user) => user.state === AgencyUserState.Active)
                      .length || 0
                  )}
                  includedSeats={
                    data.agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS
                  }
                  voiceAgents={Math.max(
                    0,
                    (data.agencyUser.agency.subscription?.numVoiceAgents ?? 1) - 1
                  )}
                />
                <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
                  <div className="px-4 py-5 sm:px-6">
                    <div className="flex items-center justify-between flex-col sm:flex-row">
                      <h3 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
                        Your Subscription
                      </h3>
                    </div>
                  </div>
                  <div className="px-4 py-5 sm:px-6">
                    <h4 className="text-base/6 font-medium mt-0 pt-0 font-body text-neutral-900 dark:text-white">
                      {getPlanName(
                        data.agencyUser.agency.subscription.plan || SubscriptionPlan.Standard,
                        data.agencyUser.agency.subscription.period || SubscriptionPeriod.Monthly,
                        data.agencyUser.agency.subscription.numSeats ||
                          Math.max(
                            data.agencyUser?.agency.subscription?.numSeatsMinimum || 3,
                            data.agencyUsers?.filter(
                              (user) => user.state === AgencyUserState.Active
                            ).length || 0
                          )
                      )}
                      <span className="ml-2">
                        <SubscriptionStateBadge state={data.agencyUser.agency.subscription.state} />
                      </span>
                    </h4>
                    <div className="mt-4">
                      <SeatProgressBar
                        totalSeats={data.agencyUser.agency.subscription.numSeats}
                        activeSeats={data.agencyUser.agency.subscription.numSeatsActive}
                        childAllocatedSeats={
                          data.childAgencies?.reduce(
                            (sum, agency) => sum + (agency.subscription?.numSeats ?? 0),
                            0
                          ) || undefined
                        }
                      />
                    </div>
                    <div
                      className={clsx(
                        canSelfServeSubscription(
                          hasPermission,
                          data.agencyUser.agency.subscription.plan
                        )
                          ? 'block'
                          : 'hidden'
                      )}
                    >
                      {data.agencyUser.agency.subscription.numSeatsActive >=
                        data.agencyUser.agency.subscription.numSeats && (
                        <div className="mt-4 rounded-md bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 px-4 py-3">
                          <p className="text-sm text-neutral-500 dark:text-neutral-400 m-0">
                            You've reached your seat limit. To invite additional users, please
                            manage your seats below.
                          </p>
                        </div>
                      )}
                      <div className="mt-8 flex items-center gap-2 flex-col sm:flex-row">
                        <Button
                          color="white"
                          onClick={() =>
                            onManageSubscription(
                              data.agencyUser?.agency.subscription?.plan || SubscriptionPlan.Pro,
                              data.agencyUser?.agency.subscription?.period ||
                                SubscriptionPeriod.Monthly,
                              data.agencyUser?.agency.subscription?.numSeats ||
                                data.agencyUser?.agency.subscription?.numSeatsMinimum ||
                                FALLBACK_MIN_SEATS
                            )
                          }
                        >
                          Manage Seats
                        </Button>
                        <Button
                          color="white"
                          onClick={() => {
                            if (
                              isLegacyStandardActiveSubscription(
                                data.agencyUser?.agency.subscription
                              )
                            ) {
                              setShowRenewalWizard(true);
                            } else {
                              setShowSelectPlanDialog(true);
                            }
                          }}
                        >
                          Change Plan
                        </Button>
                        <Button
                          color="white"
                          loading={updatePaymentLoading}
                          onClick={onUpdatePayment}
                        >
                          Update Payment
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-neutral-200 dark:divide-neutral-700 rounded-lg bg-white dark:bg-neutral-800 shadow">
                  <div className="px-4 py-5 sm:px-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
                        Manage Users
                      </h3>
                      {hasPermission(Resource.AgencyUser, Action.Create) && (
                        <Button
                          color="sky"
                          disabled={addUserBlocked}
                          // A link ignores `disabled`, so a blocked button has none.
                          href={addUserBlocked ? undefined : '/app/settings/subscription/users'}
                        >
                          Add User
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="py-5">
                    <ManageUsersList
                      agencyUser={data.agencyUser}
                      users={data.agencyUsers || []}
                      onDisable={(userId) => setDisableUserId(userId)}
                      onEnable={(userId) => setEnableUserId(userId)}
                      onEdit={(user) => setEditUser(user)}
                      scope={Scope.Organization}
                    />
                  </div>
                </div>
                {userImportEnabled &&
                  hasPermission(Resource.AgencyUser, Action.Create) &&
                  data.agencyUser && (
                    <div className="rounded-lg bg-white px-4 py-5 shadow sm:px-6 dark:bg-neutral-800">
                      <UserImport
                        users={data.agencyUsers || []}
                        agencyUser={data.agencyUser}
                        onImported={() => revalidator.revalidate()}
                        onImportingChange={setImporting}
                      />
                    </div>
                  )}
                {/* Manage Subagencies Section - only visible with FEDERATED_BILLING entitlement */}
                {data.agencyUser?.agency.entitlements.includes(
                  SubscriptionEntitlement.FederatedBilling
                ) && (
                  <div className="divide-y divide-neutral-200 dark:divide-neutral-700 rounded-lg bg-white dark:bg-neutral-800 shadow">
                    <div className="px-4 py-5 sm:px-6">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
                          Manage Subagencies
                        </h3>
                        {hasScopedPermission(
                          Scope.SubOrganization,
                          Resource.Agency,
                          Action.Create
                        ) && (
                          <Button color="sky" href="/app/settings/subscription/agencies/create">
                            Create Agency
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="py-5">
                      {(!data.childAgencies || data.childAgencies.length === 0) && (
                        <div className="px-4 py-8 sm:px-6 text-center">
                          <p className="text-sm text-neutral-500 dark:text-neutral-400">
                            No subagencies yet. Create one to get started.
                          </p>
                        </div>
                      )}
                      {data.childAgencies && data.childAgencies.length > 0 && (
                        <ul
                          role="list"
                          className="divide-y divide-neutral-200 dark:divide-neutral-700"
                        >
                          {data.childAgencies.map((agency) => (
                            <AgencyListItem
                              key={agency.id}
                              agency={agency}
                              agencyUsers={agency.users}
                            />
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          }}
        </Await>
      </React.Suspense>
      <AnimatePresence mode="wait">{outlet && React.cloneElement(outlet, {})}</AnimatePresence>
      <DisableUserDialog
        client={client}
        open={!!disableUserId}
        userId={disableUserId}
        onClose={() => setDisableUserId(null)}
        navigateTo="/app/settings/subscription"
      />
      <EnableUserDialog
        client={client}
        open={!!enableUserId}
        userId={enableUserId}
        onClose={() => setEnableUserId(null)}
        navigateTo="/app/settings/subscription"
      />
      <EditUserDialog
        client={client}
        open={!!editUser}
        user={editUser}
        onClose={() => setEditUser(null)}
        navigateTo="/app/settings/subscription"
      />
    </>
  );
};

/** The agency's users, with a team filter when the agency has teams. */
const ManageUsersList = ({
  agencyUser,
  ...listProps
}: React.ComponentProps<typeof PagedUserList> & {
  agencyUser: LoggedInAgencyUserFieldsFragment;
}) => {
  const hasTeams = agencyUser.agency.entitlements.includes(SubscriptionEntitlement.TeamManagement);
  const { data } = useGetTeamsQuery({ skip: !hasTeams });
  const teams = React.useMemo<UserListTeam[] | undefined>(
    () =>
      data?.teams
        .filter((team) => !team.archivedAt)
        .map((team) => ({
          id: team.id,
          name: team.name,
          memberIds: team.members.map((member) => member.user.id)
        })),
    [data]
  );
  return <PagedUserList {...listProps} teams={teams} />;
};

const SubscriptionPageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
          Loading settings...
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          This might take a moment.
        </p>
      </div>
    </div>
  );
};

export const AddUserDialog = () => {
  const submit = useSubmit();
  const location = useLocation();
  const navigate = useNavigate();
  const navigation = useNavigation();

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const action = useActionData() as ActionResult<CreateAgencyUserMutation>;
  const [form, setForm] = React.useState<AgencyUserInput>({
    name: '',
    email: '',
    roles: [Role.User],
    profileId: null
  });

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const { updateForm, updateFormMulti, getValidationError } = useFormActions(
    setForm,
    setValidationErrors
  );

  // The dialog exists only while its route is matched, so there is no open flag
  // to skip on: reaching the URL is the intent to read the agency's profiles.
  const { data: profileOptionsData } = useGetAgencyProfileOptionsQuery();
  const profiles = profileOptionsData?.agencyProfiles ?? [];
  const profileOptions = buildUserProfileOptions(profiles);
  const selectedProfileOption = userProfileChoiceOption({
    roles: form.roles ?? [],
    profileId: form.profileId ?? null
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    submit(form, { method: 'POST', encType: 'application/json' });
  };

  const onClose = () => {
    if (location.key === 'default') {
      navigate('/app/settings/subscription');
    } else {
      navigate(-1);
    }
  };

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (action?.applicationError) {
      setError(action.applicationError.message);
    }
  }, [action?.applicationError]);

  React.useEffect(() => {
    if (navigation.state === 'loading' && navigation.formAction === navigation.location?.pathname) {
      setLoading(false);
      if (!action?.validationErrors && !action?.applicationError) {
        setError(null);
        onClose();
      }
    }
  }, [
    navigation.state,
    navigation.formAction,
    navigation.location?.pathname,
    action?.validationErrors,
    action?.applicationError
  ]);

  return (
    <MountableDialog onClose={onClose}>
      <DialogTitle>Add User</DialogTitle>
      <DialogDescription>
        Add a new user to your agency. Once you add them, they'll be able to sign in and use Cara.
      </DialogDescription>
      <Form onSubmit={handleSubmit}>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Description>Enter the name of the user you want to add.</Description>
                <Input
                  placeholder="John Smith"
                  type="text"
                  value={form.name}
                  onChange={(e) => {
                    const name = e.currentTarget.value;
                    updateForm(['name'], (prev) => ({ ...prev, name }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['name'])}
                />
                {!!getValidationError(validationErrors, ['name']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['name'])}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Email Address</Label>
                <Description>Enter the email address of the user you want to add.</Description>
                <Input
                  placeholder="john.smith@acme.com"
                  type="email"
                  value={form.email}
                  onChange={(e) => {
                    const email = e.currentTarget.value;
                    updateForm(['email'], (prev) => ({ ...prev, email }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['email'])}
                />
                {!!getValidationError(validationErrors, ['email']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['email'])}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>{userProfileFieldLabel(profiles)}</Label>
                <Description>{userProfileFieldDescription(profiles)}</Description>
                <Combobox
                  value={selectedProfileOption}
                  minQueryLength={0}
                  maxOptions={1000}
                  onChange={(value: string) => {
                    const choice = decodeUserProfileOption(value);
                    // One control writes both fields, so it clears the errors on both.
                    updateFormMulti([['roles'], ['profileId']], (prev) => ({
                      ...prev,
                      roles: choice.roles,
                      profileId: choice.profileId
                    }));
                  }}
                  options={profileOptions}
                  error={
                    getValidationError(validationErrors, ['profileId']) ||
                    getValidationError(validationErrors, ['roles'])
                  }
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        {error && <ErrorMessage className="mt-4">{error}</ErrorMessage>}
        <DialogActions>
          <Button color="white" onClick={onClose}>
            Cancel
          </Button>
          <Button color="sky" type="submit" loading={loading}>
            Add User
          </Button>
        </DialogActions>
      </Form>
    </MountableDialog>
  );
};

export const SubscriptionStatusBanner = ({
  subscription,
  onUpdateSubscription,
  agencyUsers,
  onDisableUser,
  readOnly,
  agencyName,
  onReviewRenewalOptions,
  onDismissRenewalBanner,
  onReviewAnnualRenewal
}: {
  subscription: AgencySubscriptionFieldsFragment;
  onUpdateSubscription: () => void;
  agencyUsers?: AgencyUserFieldsFragment[];
  onDisableUser?: (userId: string) => void;
  readOnly?: boolean;
  agencyName?: string;
  onReviewRenewalOptions?: () => void;
  onDismissRenewalBanner?: () => void;
  onReviewAnnualRenewal?: () => void;
}) => {
  const annualRenewalBannerEnabled = useFlag(FEATURE_FLAGS.ANNUAL_RENEWAL_BANNER);

  // Legacy Standard sunset renewal states render here — before generic trial/overdue
  // banners — so placement and width stay consistent across banner types.
  const renewalBannerState = shouldShowRenewalMigrationBanner(subscription);
  if (renewalBannerState === 'migration') {
    return (
      <RenewalMigrationBanner
        agencyName={agencyName ?? ''}
        subscription={subscription}
        onReviewOptions={onReviewRenewalOptions}
        readOnly={readOnly || !onReviewRenewalOptions}
      />
    );
  }
  // Ahead of the sunset thank-you: an agency that confirmed the sunset in an
  // earlier term still needs this year's prompt.
  if (
    annualRenewalBannerEnabled &&
    !readOnly &&
    onReviewAnnualRenewal &&
    subscription.annualRenewalReviewOpen
  ) {
    return (
      <AnnualRenewalBanner
        agencyName={agencyName ?? ''}
        subscription={subscription}
        onReview={onReviewAnnualRenewal}
      />
    );
  }
  if (renewalBannerState === 'confirmed') {
    const nextPayment = resolveSunsetRenewalPaymentDate(subscription);
    return (
      <RenewalMigrationThankYouBanner
        planLabel={formatRenewalPlanLabel(subscription.plan)}
        periodLabel={formatRenewalPeriodLabel(subscription.period)}
        seats={subscription.numSeats}
        billingStartLabel={nextPayment?.displayLabel ?? 'your next billing date'}
        onDismiss={readOnly ? undefined : onDismissRenewalBanner}
      />
    );
  }

  if (readOnly) {
    return <SubscriptionStatusBannerReadOnly subscription={subscription} />;
  }

  if (subscription.state === SubscriptionState.Trial) {
    return (
      <div className="overflow-hidden rounded-lg bg-gradient-to-r from-sky-700 to-sky-600">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium m-0 p-0 text-neutral-50">
              Your trial is ending soon
            </h3>
            {subscription.plan === SubscriptionPlan.Enterprise ? (
              <Button
                color="white"
                href="https://meetings.hubspot.com/vic-yeh"
                target="_blank"
                rel="noopener noreferrer"
              >
                Contact Sales
              </Button>
            ) : (
              <Button color="white" onClick={onUpdateSubscription}>
                Upgrade
              </Button>
            )}
          </div>
          <p className="mt-2 max-w-2xl text-sm text-neutral-200">
            Your trial will end on{' '}
            <strong>
              {new Date(subscription.trialEndDate || new Date())?.toLocaleDateString()}
            </strong>
            . Please upgrade to a paid plan to continue using Cara.
          </p>
        </div>
      </div>
    );
  }

  if (subscription.state === SubscriptionState.Overdue) {
    return (
      <div className="overflow-hidden rounded-lg bg-gradient-to-r from-red-700 to-red-600">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium m-0 p-0 text-neutral-50">
              Your subscription payment is overdue
            </h3>
            {subscription.plan === SubscriptionPlan.Enterprise ? (
              <Button
                color="white"
                href="https://meetings.hubspot.com/vic-yeh"
                target="_blank"
                rel="noopener noreferrer"
              >
                Contact Sales
              </Button>
            ) : (
              <Button color="white" onClick={onUpdateSubscription}>
                Update Payment
              </Button>
            )}
          </div>
          <p className="mt-2 max-w-2xl text-sm text-neutral-200">
            Please make sure your selected plan and payment method are up to date in order to
            continue using Cara.
          </p>
        </div>
      </div>
    );
  }

  if (subscription.state === SubscriptionState.Expired) {
    const allUsers = agencyUsers || [];

    return (
      <div className="space-y-4">
        <div className="overflow-hidden rounded-lg bg-gradient-to-r from-neutral-700 to-neutral-600">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium m-0 p-0 text-neutral-50">
                Your subscription has expired
              </h3>
              {subscription.plan === SubscriptionPlan.Enterprise ? (
                <Button
                  color="white"
                  href="https://meetings.hubspot.com/vic-yeh"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Contact Sales
                </Button>
              ) : (
                <Button color="white" onClick={onUpdateSubscription}>
                  Restart
                </Button>
              )}
            </div>
            <p className="mt-2 max-w-2xl text-sm text-neutral-200">
              Please restart your subscription to continue using Cara. To restart with fewer seats,
              disable users below first. You cannot restart with fewer seats than your current
              number of active users.
            </p>
          </div>
        </div>
        <div className="divide-y divide-neutral-200 dark:divide-neutral-700 rounded-lg bg-white dark:bg-neutral-800 shadow">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
              Current Users
            </h3>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              Disable users to reduce the number of seats needed when restarting your subscription.
            </p>
          </div>
          <div className="py-5">
            {allUsers.length > 0 ? (
              <ul role="list" className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {[...allUsers]
                  .sort((a, b) => a.state.localeCompare(b.state))
                  .map((user) => (
                    <li
                      key={user.id}
                      className="flex justify-between px-4 py-4 sm:px-6 first:pt-0 last:pb-0"
                    >
                      <div className="flex min-w-0 gap-x-4">
                        <div className="size-12 flex-none rounded-full bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center text-neutral-700 dark:text-neutral-300 font-semibold text-lg">
                          {user.name
                            .split(' ')
                            .map((name) => name[0])
                            .join('')
                            .toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-auto">
                          <p className="text-sm/6 font-semibold text-neutral-900 dark:text-white">
                            {user.name}
                            <span className="ml-2">
                              <UserStateBadge state={user.state} />
                            </span>
                          </p>
                          <p className="flex text-xs/5 text-neutral-500 dark:text-neutral-400">
                            {user.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-x-6">
                        <Headless.Menu as="div" className="relative flex-none">
                          <Headless.MenuButton className="-m-2.5 block p-2.5 text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white">
                            <span className="sr-only">Open options</span>
                            <EllipsisVerticalIcon aria-hidden="true" className="size-5" />
                          </Headless.MenuButton>
                          <Headless.MenuItems
                            transition
                            className="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white dark:bg-neutral-800 py-2 shadow-lg ring-1 ring-neutral-900/5 dark:ring-white/10 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                          >
                            {user.state === AgencyUserState.Active && (
                              <Headless.MenuItem>
                                <span
                                  role="button"
                                  onClick={() => onDisableUser?.(user.id)}
                                  className="block px-3 py-1 text-sm/6 text-neutral-900 dark:text-white data-[focus]:bg-neutral-50 dark:data-[focus]:bg-white/5 data-[focus]:outline-none"
                                >
                                  Disable<span className="sr-only">, {user.name}</span>
                                </span>
                              </Headless.MenuItem>
                            )}
                          </Headless.MenuItems>
                        </Headless.Menu>
                      </div>
                    </li>
                  ))}
              </ul>
            ) : (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-neutral-500 dark:text-neutral-400">No users found.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (subscription.state === SubscriptionState.Paused) {
    return (
      <div className="overflow-hidden rounded-lg bg-gradient-to-r from-amber-700 to-amber-600">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium m-0 p-0 text-neutral-50">
              Your subscription is paused
            </h3>
            {subscription.plan === SubscriptionPlan.Enterprise ? (
              <Button
                color="white"
                href="https://meetings.hubspot.com/vic-yeh"
                target="_blank"
                rel="noopener noreferrer"
              >
                Contact Sales
              </Button>
            ) : (
              <Button color="white" onClick={onUpdateSubscription}>
                Resume
              </Button>
            )}
          </div>
          <p className="mt-2 max-w-2xl text-sm text-neutral-200">
            Resume your subscription to continue using Cara.
          </p>
        </div>
      </div>
    );
  }
};

const SubscriptionStatusBannerReadOnly = ({
  subscription
}: {
  subscription: AgencySubscriptionFieldsFragment;
}) => {
  if (subscription.state === SubscriptionState.Trial) {
    return (
      <div className="overflow-hidden rounded-lg bg-gradient-to-r from-sky-700 to-sky-600">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium m-0 p-0 text-neutral-50">
              Your trial is ending soon
            </h3>
          </div>
          <p className="mt-2 max-w-2xl text-sm text-neutral-200">
            Your trial will end on{' '}
            <strong>
              {new Date(subscription.trialEndDate || new Date())?.toLocaleDateString()}
            </strong>
            . Please ask your administrator to upgrade to a paid plan to continue using Cara.
          </p>
        </div>
      </div>
    );
  }

  if (subscription.state === SubscriptionState.Overdue) {
    return (
      <div className="overflow-hidden rounded-lg bg-gradient-to-r from-red-700 to-red-600">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium m-0 p-0 text-neutral-50">
              Your subscription payment is overdue
            </h3>
          </div>
          <p className="mt-2 max-w-2xl text-sm text-neutral-200">
            Please ask your administrator to make a payment in order to prevent losing access to
            Cara.
          </p>
        </div>
      </div>
    );
  }

  if (subscription.state === SubscriptionState.Expired) {
    return (
      <div className="space-y-4">
        <div className="overflow-hidden rounded-lg bg-gradient-to-r from-neutral-700 to-neutral-600">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium m-0 p-0 text-neutral-50">
                Your subscription has expired
              </h3>
            </div>
            <p className="mt-2 max-w-2xl text-sm text-neutral-200">
              Please ask your administrator to log in and restart your subscription to continue
              using Cara.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (subscription.state === SubscriptionState.Paused) {
    return (
      <div className="overflow-hidden rounded-lg bg-gradient-to-r from-amber-700 to-amber-600">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium m-0 p-0 text-neutral-50">
              Your subscription is paused
            </h3>
          </div>
          <p className="mt-2 max-w-2xl text-sm text-neutral-200">
            Please ask your administrator to resume your subscription to continue using Cara.
          </p>
        </div>
      </div>
    );
  }
};

const SubscriptionStateBadge = ({ state }: { state: SubscriptionState }) => {
  switch (state) {
    case SubscriptionState.Trial:
      return <Badge color="sky">Trial</Badge>;
    case SubscriptionState.Active:
      return <Badge color="green">Active</Badge>;
    case SubscriptionState.Expired:
      return <Badge color="neutral">Expired</Badge>;
    case SubscriptionState.Overdue:
      return <Badge color="red">Overdue</Badge>;
    case SubscriptionState.Paused:
      return <Badge color="amber">Paused</Badge>;
  }
};

const getPlanName = (plan: SubscriptionPlan, period: SubscriptionPeriod, seats: number) => {
  const periodName = period === SubscriptionPeriod.Monthly ? 'Monthly' : 'Annual';

  switch (plan) {
    case SubscriptionPlan.Standard:
      return `Cara Standard (${periodName}, ${seats} seats)`;
    case SubscriptionPlan.Basic:
      return `Cara Basic (${periodName}, ${seats} seats)`;
    case SubscriptionPlan.Pro:
      return `Cara Pro (${periodName}, ${seats} seats)`;
    case SubscriptionPlan.Enterprise:
      return `Cara Enterprise (${periodName}, ${seats} seats)`;
    default:
      return 'Unknown';
  }
};

const AgencyListItem = ({
  agency,
  agencyUsers
}: {
  agency: ChildAgencyFieldsFragment;
  agencyUsers: AgencyUserFieldsFragment[];
}) => {
  const activeSeats = agencyUsers.filter((user) => user.state === AgencyUserState.Active).length;

  return (
    <li className="flex justify-between px-4 py-4 sm:px-6 first:pt-0 last:pb-0">
      <div className="flex min-w-0 gap-x-4 flex-1">
        <div className="size-12 flex-none rounded-full bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center text-neutral-700 dark:text-neutral-300 font-semibold text-lg">
          {agency.name
            .split(' ')
            .map((word) => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2)}
        </div>
        <div className="min-w-0 flex-auto">
          <p className="text-sm/6 font-semibold text-neutral-900 dark:text-white">{agency.name}</p>
          <div className="w-[30%] min-w-32 mt-1">
            <SeatProgressBar
              totalSeats={agency.subscription?.numSeats ?? 0}
              activeSeats={activeSeats}
              size="sm"
              showLegend={false}
            />
          </div>
          <p className="text-xs/5 text-neutral-500 dark:text-neutral-400 mt-1">
            {activeSeats} of {agency.subscription?.numSeats ?? 0} seats used
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-x-6">
        <Headless.Menu as="div" className="relative flex-none">
          <Headless.MenuButton className="-m-2.5 block p-2.5 text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white">
            <span className="sr-only">Open options</span>
            <EllipsisVerticalIcon aria-hidden="true" className="size-5" />
          </Headless.MenuButton>
          <Headless.MenuItems
            transition
            className="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white dark:bg-neutral-800 py-2 shadow-lg ring-1 ring-neutral-900/5 dark:ring-white/10 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
          >
            <Headless.MenuItem>
              <Link
                href={`/app/settings/subscription/agencies/${agency.id}`}
                className="block px-3 py-1 text-sm/6 text-neutral-900 dark:text-white data-[focus]:bg-neutral-50 dark:data-[focus]:bg-white/5 data-[focus]:outline-none"
              >
                Edit Agency<span className="sr-only">, {agency.name}</span>
              </Link>
            </Headless.MenuItem>
          </Headless.MenuItems>
        </Headless.Menu>
      </div>
    </li>
  );
};

const tiers = [
  {
    name: 'Basic',
    id: SubscriptionPlan.Basic,
    href: '#',
    basePrice: { monthly: 549, annually: 5599 },
    pricePerSeat: { monthly: 150, annually: 1530 },
    description: 'The essentials to provide your best work for clients.',
    featured: false,
    contactSales: false,
    features: {
      Copilot: [
        'All built-in presets included',
        '3 custom presets included',
        '1 document style included',
        'Standard processing priority'
      ],
      'Voice AI': [
        '1 voice agent included',
        'Additional voice agents for $100/month',
        'Up to 500 minutes/month per agent',
        'Basic self-serve customization',
        'Static call notification routing',
        'Standard call priority'
      ],
      'Email AI': ['Up to 250 emails/month'],
      Automations: ['Manual preset workspace starts', 'Triggers available on the Pro plan'],
      Knowledge: ['Up to 500 pages'],
      'Security and Privacy': ['SOC2 Compliance', 'No cross-account data access for training'],
      Support: [
        'Under 3 business days response time',
        'Email and Slack support',
        'Online documentation'
      ]
    }
  },
  {
    name: 'Pro',
    id: SubscriptionPlan.Pro,
    href: '#',
    basePrice: { monthly: 749, annually: 7639 },
    pricePerSeat: { monthly: 200, annually: 2040 },
    description: 'Advanced automation and higher usage limits for growing agencies.',
    featured: true,
    contactSales: false,
    features: {
      Copilot: [
        'All built-in presets included',
        'Unlimited custom presets included',
        'Unlimited document styles included',
        'Higher processing priority'
      ],
      'Voice AI': [
        '1 voice agent included',
        'Additional voice agents for $100/month',
        'Up to 1,000 minutes/month per agent',
        'Advanced self-serve customization',
        'Dynamic call notification routing',
        'Reference customer info during the call',
        'Higher call priority'
      ],
      'Email AI': ['Up to 2,500 emails/month'],
      Automations: [
        'Email, schedule, and call-completion triggers',
        'Automatically launch preset workspaces'
      ],
      Knowledge: ['Up to 5,000 pages', 'Unlimited AMS & CRM integrations', 'Client portal app'],
      'Security and Privacy': [
        'SOC2 Compliance',
        'No cross-account data access for training',
        'Access to compliance and DPA docs'
      ],
      Support: [
        'Next business day response time',
        'Email support',
        'Online documentation',
        'Implementation consultation from the AI engineering team'
      ]
    }
  },
  {
    name: 'Enterprise',
    id: SubscriptionPlan.Enterprise,
    description: 'Dedicated support and infrastructure for your company.',
    featured: false,
    contactSales: true,
    features: {
      Copilot: [
        'All built-in presets included',
        'Unlimited custom presets included',
        'Unlimited document styles included',
        'Highest processing priority',
        'Customized workflows'
      ],
      'Voice AI': [
        '1 voice agent included',
        'Additional voice agents for $100/month',
        'Custom usage limits',
        'Fully-managed custom deployment',
        'Dynamic call notification routing',
        'Account Policy Question tool — reference customer account, policy, and service info during the call',
        'Highest call priority'
      ],
      'Email AI': ['Custom email limits'],
      Automations: [
        'Email, schedule, and call-completion triggers',
        'Automatically launch preset workspaces',
        'Custom automation limits'
      ],
      Knowledge: [
        'Custom document limits',
        'Unlimited AMS & CRM integrations',
        'Client portal app'
      ],
      'Security and Privacy': [
        'SOC2 Compliance',
        'No cross-account data access for training',
        'Access to compliance and DPA docs',
        'Custom data retention',
        'Audit logs',
        'SSO/SAML',
        'API Integrations'
      ],
      Support: [
        'Dedicated account team',
        'Email and Slack support',
        'Online documentation',
        'Custom implementation from the AI engineering team',
        'Proactive incident communication'
      ]
    }
  }
];

export const SelectPlanDialog = (props: {
  open: boolean;
  onClose: () => void;
  onSelectPlan: (plan: SubscriptionPlan, period: SubscriptionPeriod, seats?: number) => void;
  currentPlan: SubscriptionPlan;
  currentPeriod: SubscriptionPeriod;
  currentSeats: number;
  minSeats: number;
  includedSeats: number;
}) => {
  const [showDowngradeConfirmationDialog, setShowDowngradeConfirmationDialog] =
    React.useState(false);
  const [paymentFrequency, setPaymentFrequency] = React.useState<SubscriptionPeriod>(
    props.currentPeriod || SubscriptionPeriod.Annual
  );
  const [seats, setSeats] = React.useState(props.currentSeats);

  React.useEffect(() => {
    setSeats(props.currentSeats);
  }, [props.currentSeats, props.open]);

  const onSelectPlan = (plan: SubscriptionPlan, period: SubscriptionPeriod, seats?: number) => {
    if (plan === SubscriptionPlan.Basic && props.currentPlan === SubscriptionPlan.Pro) {
      setShowDowngradeConfirmationDialog(true);
    } else {
      props.onSelectPlan(plan, period, seats);
    }
  };

  return (
    <BottomSheetDialog open={props.open} onClose={props.onClose} size="6xl">
      <form>
        <h2 className="mx-auto mt-6 max-w-2xl text-center text-3xl font-medium text-pretty">
          Choose a plan.
        </h2>
        <p className="mx-auto mt-2 max-w-2xl text-center text-sm/6 text-pretty text-neutral-500 dark:text-neutral-400">
          {`Cara comes in three different tiers to fit your needs. Each tier includes ${props.includedSeats} seats and a discount for paying annually.`}
        </p>
        <div className="mt-16 flex justify-center">
          <fieldset
            aria-label="Payment frequency"
            className="inline-flex rounded-full bg-neutral-100 p-1 dark:bg-neutral-800"
          >
            <label className="relative cursor-pointer">
              <input
                type="radio"
                name="frequency"
                value={SubscriptionPeriod.Monthly}
                checked={paymentFrequency === SubscriptionPeriod.Monthly}
                onChange={(e) => setPaymentFrequency(e.target.value as SubscriptionPeriod)}
                className="sr-only peer"
              />
              <span className="block rounded-full px-4 py-1.5 text-xs font-medium text-neutral-700 peer-checked:text-white peer-checked:bg-primary-600 peer-checked:shadow-sm dark:text-neutral-300 dark:peer-checked:text-white">
                Monthly
              </span>
            </label>
            <label className="relative cursor-pointer">
              <input
                type="radio"
                name="frequency"
                value={SubscriptionPeriod.Annual}
                checked={paymentFrequency === SubscriptionPeriod.Annual}
                onChange={(e) => setPaymentFrequency(e.target.value as SubscriptionPeriod)}
                className="sr-only peer"
              />
              <span className="block rounded-full px-4 py-1.5 text-xs font-medium text-neutral-700 peer-checked:text-white peer-checked:bg-primary-600 peer-checked:shadow-sm dark:text-neutral-300 dark:peer-checked:text-white">
                Annual (15% off)
              </span>
            </label>
          </fieldset>
        </div>
        <div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              data-featured={tier.featured ? 'true' : undefined}
              className={clsx(
                'group/tier flex flex-col rounded-3xl p-8 ring-1 ring-neutral-200 data-featured:ring-2 data-featured:ring-primary-600 xl:p-10 dark:bg-neutral-800/50 dark:ring-white/15 dark:data-featured:ring-primary-400',
                tier.id === props.currentPlan && 'ring-primary-600 ring-2'
              )}
            >
              <div className="flex-1">
                <div className="flex items-center justify-between gap-x-4">
                  <h3 className="m-0 p-0 text-lg/8 font-semibold text-neutral-900 group-data-featured/tier:text-primary-600 dark:text-white dark:group-data-featured/tier:text-primary-400">
                    {tier.name}
                  </h3>
                  {tier.featured && tier.id !== props.currentPlan && (
                    <p className="rounded-full bg-primary-600/10 px-2.5 py-1 text-xs/5 font-medium text-primary-600 dark:bg-primary-500 dark:text-white">
                      Most popular
                    </p>
                  )}
                  {tier.id === props.currentPlan && (
                    <p className="rounded-full bg-primary-600/10 px-2.5 py-1 text-xs/5 font-medium text-primary-600 dark:bg-primary-500 dark:text-white">
                      Current plan
                    </p>
                  )}
                </div>
                <p className="mt-4 text-sm/6 text-neutral-600 dark:text-neutral-300">
                  {tier.description}
                </p>
                {tier.basePrice && (
                  <div className="mt-6">
                    <p className="flex items-baseline gap-x-1">
                      {paymentFrequency === SubscriptionPeriod.Annual && (
                        <span className="text-sm/6 line-through font-medium text-neutral-600 dark:text-neutral-400">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          }).format(
                            12 *
                              (tier.basePrice.monthly +
                                tier.pricePerSeat.monthly * (seats - props.includedSeats))
                          )}
                        </span>
                      )}
                      <span className="text-3xl font-semibold font-heading tracking-tight text-neutral-900 dark:text-white">
                        {paymentFrequency === SubscriptionPeriod.Monthly
                          ? new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            }).format(
                              tier.basePrice.monthly +
                                tier.pricePerSeat.monthly * (seats - props.includedSeats)
                            )
                          : new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            }).format(
                              tier.basePrice.annually +
                                tier.pricePerSeat.annually * (seats - props.includedSeats)
                            )}
                      </span>
                      <span className="text-sm/6 font-medium text-neutral-600 dark:text-neutral-400">
                        {paymentFrequency === SubscriptionPeriod.Monthly ? '/month' : '/year'}
                      </span>
                    </p>
                  </div>
                )}
                {!tier.contactSales && (
                  <div className="mt-6 mb-6">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setSeats((seats) => Math.max(props.minSeats, seats - 1))}
                        disabled={seats <= props.minSeats}
                        className={clsx(
                          'flex h-8 w-8 items-center justify-center rounded-md border',
                          seats <= props.minSeats
                            ? 'border-neutral-200 bg-neutral-50 text-neutral-300 cursor-not-allowed dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-600'
                            : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700'
                        )}
                      >
                        <MinusIcon className="h-4 w-4" />
                      </button>
                      <div className="flex-1">
                        <Input
                          type="number"
                          min={props.minSeats}
                          id="tier-seats"
                          value={seats}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            if (!isNaN(value) && value >= props.minSeats) {
                              setSeats(value);
                            }
                          }}
                          className="w-full text-center"
                        />
                      </div>
                      <label htmlFor="tier-seats">seats</label>
                      <button
                        type="button"
                        onClick={() => setSeats((seats) => Math.max(props.minSeats, seats + 1))}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {tier.contactSales ? (
                <PrimaryButton
                  className="mt-auto"
                  href="https://meetings.hubspot.com/vic-yeh"
                  target="_blank"
                >
                  Contact Sales
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  className="mt-auto"
                  onClick={() => onSelectPlan(tier.id, paymentFrequency, seats)}
                >
                  Choose plan
                </PrimaryButton>
              )}
            </div>
          ))}
        </div>
        <UsageBasedFeaturesNote className="mx-auto mt-8 max-w-md lg:mx-0 lg:max-w-none" />

        {/* Features Comparison Section */}
        <div className="my-10 max-w-7xl mx-auto hidden lg:block">
          <div className="space-y-6">
            {Object.keys(tiers.at(-1)?.features || {}).map((category) => (
              <div key={category} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {tiers.map((tier) => (
                  <div key={tier.id + category}>
                    <h3 className="font-medium text-sm text-neutral-900 dark:text-white mb-6">
                      {category}
                    </h3>
                    <ul className="space-y-3">
                      {tier.features[category].map((feature, index) => (
                        <li key={index} className="flex items-start gap-x-3">
                          <CheckIcon
                            aria-hidden="true"
                            className="h-5 w-5 flex-none text-primary-600 dark:text-primary-400 mt-0.5"
                          />
                          <span className="text-sm text-neutral-600 dark:text-neutral-300">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <DowngradeConfirmationDialog
          open={showDowngradeConfirmationDialog}
          onClose={() => setShowDowngradeConfirmationDialog(false)}
          onSelectPlan={() => {
            setShowDowngradeConfirmationDialog(false);
            props.onSelectPlan(SubscriptionPlan.Basic, paymentFrequency, seats);
          }}
        />
      </form>
    </BottomSheetDialog>
  );
};

const DowngradeConfirmationDialog = (props: {
  open: boolean;
  onClose: () => void;
  onSelectPlan: () => void;
}) => {
  return (
    <Dialog open={props.open} onClose={props.onClose}>
      <DialogTitle>Downgrade Confirmation</DialogTitle>
      <DialogDescription>
        Are you sure you want to downgrade your plan? You will lose access to all Pro features,
        including advanced customization options for Voice AI agents. You may also encounter usage
        limits on other Cara features, such as Email AI, Knowledge, and Copilot workspaces.
      </DialogDescription>
      <DialogActions>
        <Button type="button" color="white" onClick={props.onSelectPlan}>
          Downgrade to Basic
        </Button>
        <Button type="button" color="sky" onClick={props.onClose}>
          Keep Pro
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ManageSubscriptionDialog = (props: {
  open: boolean;
  onClose: () => void;
  onSubscriptionUpdated?: () => void;
  agencyUser: LoggedInAgencyUserFieldsFragment;
  plan: SubscriptionPlan;
  period: SubscriptionPeriod;
  seats: number;
  voiceAgents: number;
  minSeats: number;
  includedSeats: number;
}) => {
  const [loading, setLoading] = React.useState(false);
  const [seats, setSeats] = React.useState(props.seats || props.minSeats || FALLBACK_MIN_SEATS);
  const [voiceAgents, setVoiceAgents] = React.useState(props.voiceAgents || 0);
  const [updatePreview, setUpdatePreview] =
    React.useState<PreviewUpdateSubscriptionResultFieldsFragment | null>(null);
  const [clientSecret, setClientSecret] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (props.open) {
      setLoading(false);
      setSeats(props.seats || props.minSeats || FALLBACK_MIN_SEATS);
      setVoiceAgents(props.voiceAgents || 0);
      setUpdatePreview(null);
      setClientSecret(null);
      setError(null);
    }
  }, [props.open, props.seats, props.minSeats, props.voiceAgents]);

  const generateInvoicePreview = async (seats: number, voiceAgents: number) => {
    try {
      setLoading(true);
      const result = await client.query<
        GetSubscriptionUpdateLinkQuery,
        GetSubscriptionUpdateLinkQueryVariables
      >({
        query: GetSubscriptionUpdateLinkDocument,
        variables: {
          input: {
            plan: props.plan,
            period: props.period,
            seats: seats,
            voiceAgents: voiceAgents
          }
        }
      });
      if (result.error) {
        throw new ApolloError({ graphQLErrors: result.error.graphQLErrors });
      }
      if (result.errors?.length) {
        throw new ApolloError({ graphQLErrors: result.errors });
      }
      if (result.data?.subscriptionManagementLinks?.previewUpdateSubscription) {
        setUpdatePreview(result.data?.subscriptionManagementLinks?.previewUpdateSubscription);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!props.open) {
      return;
    }

    if (!updatePreview) {
      generateInvoicePreview(seats, voiceAgents);
      return;
    }

    const timeoutId = setTimeout(() => {
      generateInvoicePreview(seats, voiceAgents);
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [props.open, props.plan, props.period, seats, voiceAgents]);

  const onContinue = async () => {
    if (updatePreview?.checkoutUrl) {
      window.open(updatePreview.checkoutUrl, '_blank');
    } else if (updatePreview?.clientSecret) {
      setClientSecret(updatePreview.clientSecret);
    } else {
      try {
        setLoading(true);
        const result = await client.mutate<
          UpdateSubscriptionMutation,
          UpdateSubscriptionMutationVariables
        >({
          mutation: UpdateSubscriptionDocument,
          variables: {
            input: {
              plan: props.plan,
              period: props.period,
              seats: seats,
              voiceAgents: voiceAgents
            }
          }
        });
        if (result.errors?.length) {
          throw new ApolloError({ graphQLErrors: result.errors });
        }
        if (result.data?.updateSubscription?.plan) {
          props.onSubscriptionUpdated?.();
          props.onClose();
        }
      } catch (error) {
        setError(error instanceof Error ? error.message : 'An unknown error occurred');
      } finally {
        setLoading(false);
      }
    }
  };

  if (!props.plan || !props.period || !props.seats) {
    return null;
  }

  return (
    <Dialog open={props.open} onClose={props.onClose}>
      <DialogTitle>Complete Checkout</DialogTitle>
      <DialogDescription>Confirm your plan selection and complete checkout.</DialogDescription>
      {clientSecret && (
        <>
          <DialogBody>
            <Elements
              stripe={stripe}
              options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#0ea5e9',
                    colorBackground: '#ffffff',
                    colorText: '#0f172a'
                  }
                }
              }}
            >
              <SubscriptionCheckoutForm
                agencyUser={props.agencyUser}
                onSuccess={() => {
                  props.onSubscriptionUpdated?.();
                  props.onClose();
                }}
                plan={props.plan}
                period={props.period}
                seats={seats}
                voiceAgents={voiceAgents}
              />
            </Elements>
          </DialogBody>
          <DialogActions></DialogActions>
        </>
      )}
      {!clientSecret && (
        <>
          <DialogBody>
            <Form>
              <Fieldset>
                <FieldGroup>
                  <Field>
                    <Label>Plan</Label>
                    <div data-slot="control">
                      Cara {titleCase(props.plan)} ({titleCase(props.period)})
                    </div>
                  </Field>
                  <Field>
                    <Label>Seats</Label>
                    <Description>
                      Choose the number of seats you want to purchase. {props.includedSeats} seats
                      are included in the base subscription. You cannot reduce the number of seats
                      below the number of active users.
                    </Description>
                    <div data-slot="control" className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setSeats((seats) => Math.max(props.minSeats, seats - 1))}
                        disabled={seats <= props.minSeats}
                        className={clsx(
                          'flex h-8 w-8 items-center justify-center rounded-md border',
                          seats <= props.minSeats
                            ? 'border-neutral-200 bg-neutral-50 text-neutral-300 cursor-not-allowed dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-600'
                            : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700'
                        )}
                      >
                        <MinusIcon className="h-4 w-4" />
                      </button>
                      <div className="w-24">
                        <Input
                          type="number"
                          min={props.minSeats}
                          value={seats}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            if (!isNaN(value) && value >= props.minSeats) {
                              setSeats(value);
                            }
                          }}
                          className="w-full text-center"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setSeats((seats) => seats + 1)}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </Field>
                  <Field>
                    <Label>Extra Voice Agents</Label>
                    <Description>
                      Choose the number of voice agents you want to purchase. 1 voice agent is
                      already included in the base subscription.
                    </Description>
                    <div data-slot="control" className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setVoiceAgents((prev) => Math.max(0, prev - 1))}
                        disabled={voiceAgents <= 0}
                        className={clsx(
                          'flex h-8 w-8 items-center justify-center rounded-md border',
                          voiceAgents <= 0
                            ? 'border-neutral-200 bg-neutral-50 text-neutral-300 cursor-not-allowed dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-600'
                            : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700'
                        )}
                      >
                        <MinusIcon className="h-4 w-4" />
                      </button>
                      <div className="w-24">
                        <Input
                          type="number"
                          min="0"
                          value={voiceAgents}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            if (!isNaN(value) && value >= 0) {
                              setVoiceAgents(value);
                            }
                          }}
                          className="w-full text-center"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setVoiceAgents((prev) => prev + 1)}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </Field>
                </FieldGroup>
              </Fieldset>
            </Form>
            {updatePreview?.invoicePreview && (
              <div className={clsx('mt-6', loading && 'opacity-50')}>
                <InvoicePreview
                  invoicePreview={updatePreview.invoicePreview}
                  agencyUser={props.agencyUser}
                />
              </div>
            )}
          </DialogBody>
          <DialogActions>
            <Button color="white" onClick={props.onClose}>
              Cancel
            </Button>
            <Button color="sky" disabled={loading} onClick={onContinue}>
              Continue
            </Button>
          </DialogActions>
        </>
      )}

      {error && <ErrorMessage className="mt-4 whitespace-break-spaces">{error}</ErrorMessage>}
    </Dialog>
  );
};

const SubscriptionCheckoutForm = (props: {
  onSuccess: () => void;
  agencyUser: LoggedInAgencyUserFieldsFragment;
  plan: SubscriptionPlan;
  period: SubscriptionPeriod;
  seats: number;
  voiceAgents: number;
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);
  const [message, setMessage] = React.useState<string | null>(null);

  const [showConfirmSkipPaymentDialog, setShowConfirmSkipPaymentDialog] =
    React.useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    return submit(false);
  };

  const submit = async (skipPayment: boolean) => {
    if (!stripe || !elements) {
      // Stripe.js hasn't loaded yet. Make sure to disable form submission until Stripe.js has loaded
      return;
    }

    try {
      setIsProcessing(true);

      // Confirm the payment
      if (!skipPayment) {
        const result = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: window.location.href
          },
          redirect: 'if_required'
        });
        if (result.error) {
          setMessage(result.error.message || 'An unknown error occurred');
          return;
        }
      }

      // Update the subscription
      const updateResult = await client.mutate<
        UpdateSubscriptionMutation,
        UpdateSubscriptionMutationVariables
      >({
        mutation: UpdateSubscriptionDocument,
        variables: {
          input: {
            plan: props.plan,
            period: props.period,
            seats: props.seats,
            voiceAgents: props.voiceAgents
          }
        }
      });
      if (updateResult.errors?.length) {
        throw new ApolloError({ graphQLErrors: updateResult.errors });
      }
      if (updateResult.data?.updateSubscription?.plan) {
        props.onSuccess();
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement
        options={{
          layout: { type: 'tabs' },
          business: { name: props.agencyUser.agency.name },
          fields: {
            billingDetails: { name: 'auto', email: 'auto', phone: 'auto', address: 'if_required' }
          },
          terms: {
            card: 'always',
            usBankAccount: 'always'
          }
        }}
      />

      <div className="flex flex-col gap-2">
        <PrimaryButton loading={isProcessing || !stripe || !elements} type="submit">
          Confirm Subscription
        </PrimaryButton>

        {props.agencyUser.agency.subscription?.state === SubscriptionState.Trial && (
          <a
            href="#"
            onClick={() => setShowConfirmSkipPaymentDialog(true)}
            className="text-xs text-sky-700 dark:text-sky-300 hover:text-sky-900 dark:hover:text-sky-200 underline text-center"
          >
            Skip payment method and update subscription
          </a>
        )}
      </div>

      <Dialog
        open={showConfirmSkipPaymentDialog}
        onClose={() => setShowConfirmSkipPaymentDialog(false)}
        size="sm"
      >
        <DialogTitle>Skip Payment Method</DialogTitle>
        <DialogDescription>
          You can add seats to your subscription during your trial without a payment method to
          invite additional users to try Cara. However, you will need to add a payment method prior
          to the end of your trial to continue using Cara. You can add one now to avoid service
          disruption and you won't be charged until your trial ends.
        </DialogDescription>
        <DialogActions>
          <Button color="white" onClick={() => setShowConfirmSkipPaymentDialog(false)}>
            Cancel
          </Button>
          <Button
            color="sky"
            onClick={() => {
              setShowConfirmSkipPaymentDialog(false);
              submit(true);
            }}
          >
            Skip Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Show error message to your customers */}
      {message && <ErrorMessage className="mt-2">{message}</ErrorMessage>}
    </form>
  );
};
